import type { Node } from '@react-types/shared';
import { createStore } from '@xstate/store';
import { createContext, useContext } from 'react';
import type { Key } from 'react-aria-components';

// Store -----------------------------------------------------------------------

const initialStoreValue = {
  direction: 'none' as TransitionDirection,
  isReady: false,
  stack: [] as Key[],
  transition: 'cross-fade' as TransitionType,
};

export const NAVIGATION = createStore({
  context: initialStoreValue,
  on: {
    initialise: (
      context,
      event: { stack: Key[]; transition: TransitionType }
    ) => ({
      ...context,
      isReady: true,
      stack: event.stack,
      transition: event.transition,
    }),
    reset: () => initialStoreValue,

    push: (context, event: { id: Key }) => ({
      ...context,
      stack: [...context.stack, event.id],
      direction: isDirectional(context.transition)
        ? 'forward'
        : context.direction,
    }),
    pop: (context) => ({
      ...context,
      stack: context.stack.slice(0, -1),
      direction: isDirectional(context.transition)
        ? 'backward'
        : context.direction,
    }),
    replace: (context, event: { id: Key; direction: TransitionDirection }) => ({
      ...context,
      stack: unshiftDuplicates([...context.stack.slice(0, -1), event.id]),
      direction: event.direction,
    }),
  },
});

function isDirectional(type: TransitionType): type is 'slide' | 'cross-fade' {
  return type === 'slide' || type === 'cross-fade';
}

/**
 * Unshift duplicates to prevent rare case where duplicate entries can end up
 * in the stack e.g. initialStack = ['one']
 * - push('two') → ['one', 'two']
 * - replace('one') → ['one', 'one'] (Oh no!)
 */
function unshiftDuplicates<T>(arr: T[]): T[] {
  if (arr.length <= 1) {
    return arr;
  }

  const last = arr[arr.length - 1];
  const secondLast = arr[arr.length - 2];

  if (last === secondLast) {
    return arr.slice(0, -1);
  }

  return arr;
}

// Context ---------------------------------------------------------------------

export const DIRECTION = { forward: 1, backward: -1, none: 0 } as const;

export type TransitionDirection = keyof typeof DIRECTION;
export type TransitionType = 'instant' | 'fade' | 'cross-fade' | 'slide';

export type DialogRouteState<T> = {
  /**
   * All route IDs in the collection.
   */
  get routeIDs(): Iterable<Key>;
  /**
   * Whether pop navigation is allowed. This is determined by the `allowPopNavigation`
   * prop, the current route ID, and the stack length.
   *
   * When the stack contains a single item this value will resolve to `false`,
   * regardless of configuration.
   */
  get isPopAllowed(): boolean;
  /**
   * The current route ID.
   */
  get currentRouteId(): Key;
  /**
   * The next route ID.
   */
  get nextRouteId(): Key | null;
  /**
   * The previous route ID.
   */
  get prevRouteId(): Key | null;
  /**
   * The current selected item.
   */
  get selectedItem(): Node<T> | null;
  /**
   * The current history stack.
   */
  get stack(): Key[];
  /**
   * Push a new route onto the stack.
   */
  push(id: Key): void;
  /**
   * Pop the last route from the stack.
   */
  pop(): void;
  /**
   * Replace the current route with a new one. The second argument is only
   * relevant when using directional transitions ("slide" and "cross-fade"), it
   * defaults to `'forward'`.
   */
  replace(id: Key, direction?: TransitionDirection): void;
};

export const DialogRouteStateContext =
  createContext<DialogRouteState<object> | null>(null);

/**
 * A convenience hook to access dialog route state. Useful in situations where
 * a component is shared between routes, or state must be composed with other
 * hooks.
 */
export function useDialogRouteState() {
  return useContext(DialogRouteStateContext);
}
